<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-large"
  >
    <template #header>
      <div class="d-flex justify-space-between w-100">
        <v-card-title class="screen-title"
          >インターネットアプライアンス ゾーン選択</v-card-title
        >
        <button
          type="button"
          aria-label="Close"
          class="close"
          @click="closeModal"
        >
          ×
        </button>
      </div>
    </template>
    <template v-if="isLoaded" v-slot:body>
      <!-- BODY -->
      <v-card-text class="pa-0">
        <div class="modal-body">
          <div class="d-flex flex-wrap ga-3 justify-end my-4">
            <v-btn color="primary" @click="downloadConfig" v-permission.config
              >設定ダウンロード
            </v-btn>
            <v-btn color="primary" @click="uploadConfig" v-permission.config
              >設定アップロード
            </v-btn>
          </div>
          <app-table
            primary-key="zoneRelationId"
            :fields="zoneRelations.fields"
            :items="displayRelationList"
            :hover="true"
            v-model:table-sort-by="zoneRelations.sortBy"
            :per-page="-1"
            @click:row="showPolicy"
          >
            <template #cell(direction)> → </template>
          </app-table>
        </div>
        <v-alert class="my-4" v-if="!!uploadAlert">{{ uploadAlert }}</v-alert>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn
              variant="flat"
              color="primary"
              @click="putPolicy"
              v-permission.config
            >
              ポリシー設定反映
            </v-btn>
            <v-btn variant="outlined" color="primary" @click="closeModal">
              キャンセル
            </v-btn>
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import type { ZoneRelation, ZoneRelationItem } from "@/apis/ZoneRelationApi";
import type {
  GetDstAddress1,
  GetDstAddress2,
  GetPolicy,
  GetPolicyType1,
  GetSrcAddress1,
  GetSrcAddress2,
  PolicyType,
  PutDstAddress1,
  PutDstAddress2,
  PutPolicyType1,
  PutSrcAddress1,
  PutSrcAddress2,
} from "@/apis/NwPolicyApi";
import type { Type1Service } from "@/apis/ContractChangeType1Api";
import { UploadError } from "@/services/ConfigDownloadService";
import { mapState } from "vuex";
import type { AppTableHeader, SortItem } from "@/components/AppTable.vue";
import InternetType1PolicyUtmList from "@/modals/applianceContractSetting/type1/policy/InternetType1PolicyUtmList.vue";
import InternetType1PolicyIfwList from "@/modals/applianceContractSetting/type1/policy/InternetType1PolicyIfwList.vue";
import { VueFinalModal } from "vue-final-modal";
import InternetType1PolicyUtmConfirm from "@/modals/applianceContractSetting/type1/policy/InternetType1PolicyUtmConfirm.vue";
import InternetType1PolicyIfwConfirm from "@/modals/applianceContractSetting/type1/policy/InternetType1PolicyIfwConfirm.vue";

export default defineComponent({
  name: "InternetType1ZoneSelectReference",
  props: {
    /** サービスメニュー(インターネットType1) */
    type1Service: {
      type: Object as PropType<Type1Service>,
      required: true,
    },
  },
  data() {
    return {
      /** 初期通信完了フラグ */
      isLoaded: false,
      relations: {} as ZoneRelation,
      policy: {} as GetPolicyType1,
      /** モーダル入室時のスナップショット */
      originalPolicy: {} as GetPolicyType1,
      zoneRelations: {
        sortBy: { key: "zone_src", order: "asc" } as SortItem,
        fields: [
          {
            key: "zone_src",
            title: "送信元ゾーン/ゾーン名",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: ZoneRelationItem) => {
              return (
                this.$filter.enumTo(item.srcZoneType, "zoneType") +
                "/" +
                item.srcZoneName
              );
            },
          },
          {
            key: "direction",
            title: "",
          },
          {
            key: "zone_dest",
            title: "宛先ゾーン/ゾーン名",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: ZoneRelationItem) => {
              return (
                this.$filter.enumTo(item.dstZoneType, "zoneType") +
                "/" +
                item.dstZoneName
              );
            },
          },
        ] as AppTableHeader<ZoneRelationItem>[],
      },
      uploadAlert: null as string | null,
    };
  },
  async mounted() {
    await this.load();
    this.originalPolicy = cloneDeep(this.policy);
    this.isLoaded = true;
  },
  computed: {
    ...mapState("user", {
      contractSummary: "contractSummary",
    }),
    displayRelationList(): ZoneRelationItem[] {
      // global -> private
      // global -> extra
      // extra -> global
      // private -> global
      // 上記のみ表示
      return this.relations.zoneRelationList.filter(
        (v) =>
          (v.srcZoneType === "INTERNET" &&
            (v.dstZoneType === "PRIVATE" || v.dstZoneType === "EXTRA")) ||
          ((v.srcZoneType === "PRIVATE" || v.srcZoneType === "EXTRA") &&
            v.dstZoneType === "INTERNET")
      );
    },
    /** 変更があるかどうかを判定（policyKeyIdは比較から除外） */
    hasChanges(): boolean {
      const normalize = (p: GetPolicyType1) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { policyKeyId, ...rest } = p ?? ({} as GetPolicyType1);

        // NAT契約がある場合、子componentで追加された空エントリを除外
        if (this.contractSummary.isNat && rest.policyNatType1List) {
          rest.policyNatType1List = rest.policyNatType1List.filter((item) => {
            // 内容がある場合は保持
            if (item.total > 0 || item.policyList.length > 0) {
              return true;
            }

            // 空エントリの場合、originalPolicyに存在するかチェック
            // ※ originalに存在 = API由来 → 保持、originalに不存在 = 子component追加 → 除外
            const existsInOriginal =
              this.originalPolicy.policyNatType1List?.some(
                (orig) => orig.zoneRelationId === item.zoneRelationId
              );
            return existsInOriginal;
          });
        }

        return rest;
      };
      return !isEqual(normalize(this.policy), normalize(this.originalPolicy));
    },
    /** ポリシー種別 */
    policyType(): PolicyType {
      if (this.type1Service.applianceType === "UTM") {
        return this.contractSummary.isNat
          ? "UTM_IPMASQUERADE_NAT"
          : "UTM_IPMASQUERADE";
      } else {
        return this.contractSummary.isNat
          ? "IFW_IPMASQUERADE_NAT"
          : "IFW_IPMASQUERADE";
      }
    },
  },
  methods: {
    /** ×ボタン／キャンセル押下時の共通クローズ処理 */
    async closeModal(): Promise<void> {
      if (!this.hasChanges) {
        (
          this.$refs.modal as typeof VueFinalModal & { cancel: () => void }
        ).cancel();
        return;
      }
      try {
        await this.$confirm("変更を破棄してキャンセルします、よろしいですか？");
        (
          this.$refs.modal as typeof VueFinalModal & { cancel: () => void }
        ).cancel();
      } catch {
        return;
      }
    },
    /** データ取得 */
    async load() {
      this.relations = await this.$api.zoneRelation.getZoneRelation();
      this.policy = await this.$api.nwPolicy.getType1Policies(this.policyType);
    },
    /** 設定ダウンロード */
    downloadConfig() {
      this.$service.configDownload.downloadInternetType1Policy(
        this.type1Service.vpnCode,
        this.type1Service.applianceType,
        this.policy,
        this.relations.zoneRelationList
      );
    },
    /** 設定アップロード */
    async uploadConfig() {
      try {
        const uploadPolicy =
          await this.$service.configDownload.uploadInternetType1Policy(
            this.type1Service.vpnCode,
            this.type1Service.applianceType,
            this.policy,
            this.relations.zoneRelationList,
            this.$store.state.user.contractSummary.isNat
          );
        this.uploadAlert = null;
        await this.$confirm(
          "ポリシー設定を上書きします。設定反映していない設定は破棄されます。よろしいですか？"
        );
        this.policy = uploadPolicy;
      } catch (e) {
        this.uploadAlert = (e as UploadError).message;
      }
    },
    async putPolicy() {
      this.uploadAlert = null;
      await this.$modal.show(
        this.type1Service.applianceType === "UTM"
          ? InternetType1PolicyUtmConfirm
          : InternetType1PolicyIfwConfirm,
        {
          type1Policy: this.policy,
          type1Service: this.type1Service,
          zoneRelationList: this.relations.zoneRelationList,
        }
      );
      await this.$api.nwPolicy.putType1Policies(
        get2putPolicy(this.policy),
        this.policyType
      );
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    async showPolicy(item: ZoneRelationItem) {
      this.uploadAlert = null;
      this.policy = await this.$modal.show(
        this.type1Service.applianceType === "UTM"
          ? InternetType1PolicyUtmList
          : InternetType1PolicyIfwList,
        {
          zoneRelationItem: item,
          policy: this.policy,
          type1Service: this.type1Service,
        }
      );
    },
  },
});

/** type1ポリシーGETをPUT用のポリシーに変換 */
export function get2putPolicy(type1policy: GetPolicyType1): PutPolicyType1 {
  const toSrcAddress1 = (src: GetSrcAddress1): PutSrcAddress1 => ({
    srcAddressList: src.srcAddressList.map((e) => e.ipAddressId!),
    srcFqdnList: src.srcFqdnList.map((e) => e.fqdnId),
    srcCountryList: src.srcCountryList.map((e) => e.countrySeq),
  });
  const toDstAddress1 = (dst: GetDstAddress1): PutDstAddress1 => ({
    dstAddressList: dst.dstAddressList.map((e) => e.ipAddressId!),
  });
  const toSrcAddress2 = (src: GetSrcAddress2): PutSrcAddress2 => ({
    srcAddressList: src.srcAddressList.map((e) => e.ipAddressId!),
  });
  const toDstAddress2 = (dst: GetDstAddress2): PutDstAddress2 => ({
    dstAddressList: dst.dstAddressList.map((e) => e.ipAddressId!),
    dstFqdnList: dst.dstFqdnList.map((e) => e.fqdnId),
    dstCountryList: dst.dstCountryList.map((e) => e.countrySeq),
  });
  const toDefaultService = (s: GetPolicy["defaultServiceList"]): string[] =>
    s.map((e) => e.serviceSeq);
  const toCustomService = (s: GetPolicy["customServiceList"]): string[] =>
    s.map((e) => e.customServiceSeq);

  return {
    policyUtmType1: type1policy.policyUtmType1
      ? {
          internetToAccessPointList:
            type1policy.policyUtmType1.internetToAccessPointList.map((v) => {
              return {
                zoneRelationId: v.zoneRelationId,
                policyList: v.policyList
                  .filter(
                    (e) => e.policyCategoryInfo !== "UNEDITABLE_DEFAULT_POLICY"
                  )
                  .map((policy) => ({
                    ...policy,
                    srcAddress: toSrcAddress1(policy.srcAddress),
                    dstAddress: toDstAddress1(policy.dstAddress),
                    defaultServiceList: toDefaultService(
                      policy.defaultServiceList
                    ),
                    customServiceList: toCustomService(
                      policy.customServiceList
                    ),
                  })),
              };
            }),
          accessPointToInternetList:
            type1policy.policyUtmType1.accessPointToInternetList.map((v) => {
              return {
                zoneRelationId: v.zoneRelationId,
                policyList: v.policyList
                  .filter(
                    (e) => e.policyCategoryInfo !== "UNEDITABLE_DEFAULT_POLICY"
                  )
                  .map((policy) => ({
                    ...policy,
                    srcAddress: toSrcAddress2(policy.srcAddress),
                    dstAddress: toDstAddress2(policy.dstAddress),
                    defaultServiceList: toDefaultService(
                      policy.defaultServiceList
                    ),
                    customServiceList: toCustomService(
                      policy.customServiceList
                    ),
                  })),
              };
            }),
        }
      : null,
    policyIfwType1: type1policy.policyIfwType1
      ? {
          internetToAccessPointList:
            type1policy.policyIfwType1.internetToAccessPointList.map((v) => {
              return {
                zoneRelationId: v.zoneRelationId,
                policyList: v.policyList
                  .filter(
                    (e) => e.policyCategoryInfo !== "UNEDITABLE_DEFAULT_POLICY"
                  )
                  .map((policy) => ({
                    ...policy,
                    srcAddress: toSrcAddress1(policy.srcAddress),
                    dstAddress: toDstAddress1(policy.dstAddress),
                    defaultServiceList: toDefaultService(
                      policy.defaultServiceList
                    ),
                    customServiceList: toCustomService(
                      policy.customServiceList
                    ),
                  })),
              };
            }),
          accessPointToInternetList:
            type1policy.policyIfwType1.accessPointToInternetList.map((v) => {
              return {
                zoneRelationId: v.zoneRelationId,
                policyList: v.policyList
                  .filter(
                    (e) => e.policyCategoryInfo !== "UNEDITABLE_DEFAULT_POLICY"
                  )
                  .map((policy) => ({
                    ...policy,
                    srcAddress: toSrcAddress2(policy.srcAddress),
                    dstAddress: toDstAddress2(policy.dstAddress),
                    defaultServiceList: toDefaultService(
                      policy.defaultServiceList
                    ),
                    customServiceList: toCustomService(
                      policy.customServiceList
                    ),
                  })),
              };
            }),
        }
      : null,
    policyIPMasqueradeType1List: type1policy.policyIPMasqueradeType1List.map(
      (v) => {
        return {
          zoneRelationId: v.zoneRelationId,
          policyList: v.policyList
            .filter((e) => e.policyCategoryInfo !== "UNEDITABLE_DEFAULT_POLICY")
            .map((policy) => ({
              ...policy,
              srcAddressList: policy.srcAddressList.map((e) => e.ipAddressId!),
              dstAddressList: policy.dstAddressList.map((e) => e.ipAddressId!),
              ipPoolList: policy.ipPoolList.map((e) => e.ipPoolSeq),
            })),
        };
      }
    ),
    policyNatType1List: type1policy.policyNatType1List.map((v) => {
      return {
        zoneRelationId: v.zoneRelationId,
        policyList: v.policyList.map((policy) => ({
          ...policy,
          srcAddressList: policy.srcAddressList.map((e) => e.ipAddressId!),
        })),
      };
    }),
    policyKeyId: type1policy.policyKeyId,
  };
}
</script>
